import { toast } from "react-toastify";

import BulkAction from "common/components/BulkAction";
import ProfileImage from "common/components/ui/ProfileImage";
import type { LinkedInProfile } from "common/types";

import useBlacklistMutations from "../../datahooks/useBlacklistMutations";
import RemoveFromBlacklist from "./RemoveFromBlacklist";

import type { Company } from "../../types";

interface BulkRemoveFromBlacklistProps {
  selectedItems: (Company | LinkedInProfile)[];
  onBack: () => void;
  onSuccess: () => void;
  blacklistType: "accounts" | "companies";
}

export default function BulkRemoveFromBlacklist({
  selectedItems,
  onBack,
  onSuccess,
  blacklistType,
}: BulkRemoveFromBlacklistProps) {
  const { removeFromBlacklist } = useBlacklistMutations();

  function handleRemove() {
    const itemsWithId = selectedItems.map((item) =>
      "id" in item ? item : { ...item, id: item.urn },
    );

    //this must be declared here in order for the reference to stay the same throughout BulkAction re-renders
    const actionFn = (
      item: (typeof itemsWithId)[number],
      abortSignal: AbortSignal,
    ) =>
      removeFromBlacklist(
        blacklistType === "accounts"
          ? { accountUrn: item.urn, abortSignal }
          : { companyUrn: item.urn, abortSignal },
      );

    toast(
      ({ closeToast }) => (
        <BulkAction<(typeof itemsWithId)[number]>
          actionFn={actionFn}
          items={itemsWithId}
          onClose={closeToast}
          renderTitle={(finishedCount) =>
            finishedCount === selectedItems.length
              ? "Removed from blacklist"
              : `Removed ${finishedCount}/${selectedItems.length} from blacklist`
          }
          renderReviewItem={(item) => {
            const isAccount = "picture_url" in item;
            return (
              <div className="flex border border-black-200 p-3 gap-x-2 rounded-3xl">
                <ProfileImage
                  src={isAccount ? item.picture_url : item.logo_url}
                  size="sm"
                />
                <span className="text-sm-bold truncate">
                  {isAccount ? item.full_name : item.name}
                </span>
              </div>
            );
          }}
        />
      ),
      {
        position: "bottom-right",
        autoClose: false,
        closeButton: false,
        icon: false,
      },
    );
    onSuccess();
  }

  return (
    <RemoveFromBlacklist
      selectedItems={selectedItems}
      onBack={onBack}
      onRemove={handleRemove}
      blacklistType={blacklistType}
      isLoading={false}
    />
  );
}
